/**
 * Complete Truss Profile Library
 * Based on Global Truss specifications (www.globaltruss.com)
 * All load/deflection data converted from PDF (imperial → metric)
 * 
 * Units: Length=m, Dimensions=mm, Load=kg, Deflection=mm
 */

// Available standard truss segment lengths (meters)
export const STANDARD_LENGTHS = [0.5, 1, 1.25, 1.5, 2, 2.5, 3, 3.5, 4];

// Complete truss profile library
export const TRUSS_LIBRARY = {
  F14: {
    id: 'F14',
    label: 'F14 – 100mm Square',
    geometry: 'square',
    chordCount: 4,
    size_mm: 100,
    mainTubeDiam_mm: 20,
    mainTubeWall_mm: 2,
    braceDiam_mm: 6,
    braceWall_mm: 2,
    material: 'EN-AW 6082 T6',
    weightPerMeter: 4.5,
    bayInterval: 0.3,
    allowedStructures: ['single', 'goalpost', 'box'],
    loadTable: [
      { span: 2,  udl: 425,  pointLoad: 147,  deflection: 12.7 },
      { span: 4,  udl: 244,  pointLoad: 93,   deflection: 40.6 },
      { span: 6,  udl: 174,  pointLoad: 74,   deflection: 91.4 },
      { span: 8,  udl: 112,  pointLoad: 52,   deflection: 154.9 },
    ],
    availableLengths: [0.5, 1, 1.5, 2, 2.5, 3],
  },
  F23: {
    id: 'F23',
    label: 'F23 – 220mm Triangle',
    geometry: 'triangle',
    chordCount: 3,
    size_mm: 220,
    mainTubeDiam_mm: 35,
    mainTubeWall_mm: 2,
    braceDiam_mm: 8,
    braceWall_mm: 2,
    material: 'EN-AW 6082 T6',
    weightPerMeter: 5.2,
    bayInterval: 0.4,
    allowedStructures: ['single'],
    loadTable: [
      { span: 2,  udl: 350,  pointLoad: 262,  deflection: 2.5 },
      { span: 3,  udl: 342,  pointLoad: 171,  deflection: 5.1 },
      { span: 4,  udl: 255,  pointLoad: 127,  deflection: 10.2 },
      { span: 6,  udl: 116,  pointLoad: 81,   deflection: 20.3 },
      { span: 8,  udl: 112,  pointLoad: 56,   deflection: 35.6 },
      { span: 10, udl: 80,   pointLoad: 41,   deflection: 55.9 },
    ],
    availableLengths: [0.5, 1, 1.5, 2, 2.5, 3, 4],
  },
  F24: {
    id: 'F24',
    label: 'F24 – 220mm Square',
    geometry: 'square',
    chordCount: 4,
    size_mm: 220,
    mainTubeDiam_mm: 35,
    mainTubeWall_mm: 2,
    braceDiam_mm: 8,
    braceWall_mm: 2,
    material: 'EN-AW 6082 T6',
    weightPerMeter: 5.8,
    bayInterval: 0.4,
    allowedStructures: ['single', 'goalpost', 'box'],
    loadTable: [
      { span: 2,  udl: 404,  pointLoad: 405,  deflection: 0 },
      { span: 3,  udl: 402,  pointLoad: 354,  deflection: 2.5 },
      { span: 4,  udl: 400,  pointLoad: 286,  deflection: 7.6 },
      { span: 6,  udl: 390,  pointLoad: 195,  deflection: 22.9 },
      { span: 8,  udl: 280,  pointLoad: 141,  deflection: 40.6 },
      { span: 10, udl: 210,  pointLoad: 107,  deflection: 66.0 },
    ],
    availableLengths: [0.5, 1, 1.5, 2, 2.5, 3, 4],
  },
  F31: {
    id: 'F31',
    label: 'F31 – Single Tube',
    geometry: 'tube',
    chordCount: 1,
    size_mm: 50,
    mainTubeDiam_mm: 50,
    mainTubeWall_mm: 2,
    braceDiam_mm: 0,
    braceWall_mm: 0,
    material: 'EN-AW 6082 T6',
    weightPerMeter: 2.8,
    bayInterval: 0.5,
    allowedStructures: ['single'],
    loadTable: [
      { span: 1,  udl: 438,  pointLoad: 219,  deflection: 10.2 },
      { span: 2,  udl: 218,  pointLoad: 109,  deflection: 38.1 },
      { span: 3,  udl: 147,  pointLoad: 73,   deflection: 83.8 },
      { span: 4,  udl: 108,  pointLoad: 55,   deflection: 149.9 },
    ],
    availableLengths: [0.5, 1, 1.5, 2, 2.5, 3],
  },
  F32: {
    id: 'F32',
    label: 'F32 – 290mm I-Beam/Ladder',
    geometry: 'ladder',
    chordCount: 2,
    size_mm: 290,
    mainTubeDiam_mm: 50,
    mainTubeWall_mm: 2,
    braceDiam_mm: 20,
    braceWall_mm: 2,
    material: 'EN-AW 6082 T6',
    weightPerMeter: 5.45,
    bayInterval: 0.5,
    allowedStructures: ['single', 'goalpost', 'box'],
    loadTable: [
      { span: 2,  udl: 1346,  pointLoad: 673,  deflection: 2.5 },
      { span: 4,  udl: 664,   pointLoad: 333,  deflection: 10.2 },
      { span: 6,  udl: 456,   pointLoad: 228,  deflection: 22.9 },
      { span: 8,  udl: 344,   pointLoad: 170,  deflection: 40.6 },
      { span: 10, udl: 269,   pointLoad: 135,  deflection: 63.5 },
    ],
    availableLengths: [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4],
  },
  F33: {
    id: 'F33',
    label: 'F33 – 290mm Triangle Heavy',
    geometry: 'triangle',
    chordCount: 3,
    size_mm: 290,
    mainTubeDiam_mm: 50,
    mainTubeWall_mm: 2,
    braceDiam_mm: 20,
    braceWall_mm: 2,
    material: 'EN-AW 6082 T6',
    weightPerMeter: 6.36,
    bayInterval: 0.5,
    allowedStructures: ['single'],
    loadTable: [
      { span: 2,  udl: 1522, pointLoad: 761,  deflection: 2.5 },
      { span: 4,  udl: 824,  pointLoad: 412,  deflection: 12.7 },
      { span: 6,  udl: 552,  pointLoad: 276,  deflection: 27.9 },
      { span: 8,  udl: 411,  pointLoad: 206,  deflection: 48.3 },
      { span: 10, udl: 309,  pointLoad: 154,  deflection: 76.2 },
    ],
    availableLengths: [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4],
  },
  F34: {
    id: 'F34',
    label: 'F34 – 290mm Square',
    geometry: 'square',
    chordCount: 4,
    size_mm: 290,
    mainTubeDiam_mm: 50,
    mainTubeWall_mm: 2,
    braceDiam_mm: 20,
    braceWall_mm: 2,
    material: 'EN-AW 6082 T6',
    weightPerMeter: 8.17,
    bayInterval: 0.5,
    allowedStructures: ['single', 'goalpost', 'box'],
    loadTable: [
      { span: 2,  udl: 2848, pointLoad: 1919, deflection: 2.5 },
      { span: 4,  udl: 1972, pointLoad: 1066, deflection: 12.7 },
      { span: 6,  udl: 1335, pointLoad: 670,  deflection: 33.0 },
      { span: 8,  udl: 1017, pointLoad: 512,  deflection: 58.4 },
      { span: 10, udl: 790,  pointLoad: 394,  deflection: 91.4 },
      { span: 12, udl: 612,  pointLoad: 308,  deflection: 104.1 },
    ],
    availableLengths: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3, 3.5, 4],
  },
  DT34P: {
    id: 'DT34P',
    label: 'DT34P – 290mm Square Heavy',
    geometry: 'square',
    chordCount: 4,
    size_mm: 290,
    mainTubeDiam_mm: 50,
    mainTubeWall_mm: 3,
    braceDiam_mm: 20,
    braceWall_mm: 2,
    material: 'EN-AW 6082 T6',
    weightPerMeter: 10.5,
    bayInterval: 0.5,
    allowedStructures: ['single', 'goalpost', 'box'],
    loadTable: [
      { span: 2,  udl: 2840, pointLoad: 2840, deflection: 2.5 },
      { span: 4,  udl: 2827, pointLoad: 1544, deflection: 12.7 },
      { span: 6,  udl: 2041, pointLoad: 1033, deflection: 33.0 },
      { span: 8,  udl: 1521, pointLoad: 771,  deflection: 61.0 },
      { span: 10, udl: 1206, pointLoad: 608,  deflection: 91.4 },
      { span: 12, udl: 981,  pointLoad: 491,  deflection: 109.2 },
    ],
    availableLengths: [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4],
  },
  DT44P: {
    id: 'DT44P',
    label: 'DT44P – 400mm Square Heavy',
    geometry: 'square',
    chordCount: 4,
    size_mm: 400,
    mainTubeDiam_mm: 50,
    mainTubeWall_mm: 3,
    braceDiam_mm: 25,
    braceWall_mm: 2,
    material: 'EN-AW 6082 T6',
    weightPerMeter: 14.2,
    bayInterval: 0.5,
    allowedStructures: ['single', 'goalpost', 'box'],
    loadTable: [
      { span: 2,  udl: 3610, pointLoad: 1806, deflection: 1.3 },
      { span: 4,  udl: 2380, pointLoad: 1190, deflection: 5.1 },
      { span: 6,  udl: 1643, pointLoad: 823,  deflection: 15.2 },
      { span: 8,  udl: 1336, pointLoad: 653,  deflection: 27.9 },
      { span: 10, udl: 1095, pointLoad: 532,  deflection: 45.7 },
      { span: 12, udl: 907,  pointLoad: 436,  deflection: 68.6 },
      { span: 14, udl: 723,  pointLoad: 364,  deflection: 94.0 },
      { span: 16, udl: 609,  pointLoad: 304,  deflection: 124.5 },
    ],
    availableLengths: [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4],
  },
};

// Junction types
export const JUNCTION_TYPES = [
  { id: 'straight', label: 'Straight Connector', description: 'End-to-end connection' },
  { id: '2way', label: '2-Way Corner (90°)', description: '90 degree corner' },
  { id: '3way', label: '3-Way Corner', description: 'T-shape junction' },
  { id: '4way', label: '4-Way Corner (Box)', description: 'Box corner junction' },
  { id: 'tjunction', label: 'T-Junction', description: 'Perpendicular branch' },
];

// =============================================
// CORNER BLOCK CATALOG (Global Truss)
// =============================================
export const CORNER_BLOCKS = {
  // --- F34 / DT34P / DT44P compatible (Square 290mm+) ---
  'SQ-4109': {
    id: 'SQ-4109', label: 'SQ-4109', type: '2way', angle: 90,
    description: '2-Way 90° Corner Block',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 2.8, size_mm: 290,
  },
  'SQ-4110': {
    id: 'SQ-4110', label: 'SQ-4110', type: '2way', angle: 90,
    description: '2-Way 90° Corner (Compact)',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 2.5, size_mm: 290,
  },
  'SQ-4118': {
    id: 'SQ-4118', label: 'SQ-4118', type: '2way', angle: 135,
    description: '2-Way 135° Corner Block',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 3.0, size_mm: 290,
  },
  'SQ-4119': {
    id: 'SQ-4119', label: 'SQ-4119', type: '2way', angle: 120,
    description: '2-Way 120° Corner Block',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 2.9, size_mm: 290,
  },
  'SQ-4121': {
    id: 'SQ-4121', label: 'SQ-4121', type: '3way', angle: 90,
    description: '3-Way T-Junction Corner',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 4.5, size_mm: 290,
  },
  'SQ-4122': {
    id: 'SQ-4122', label: 'SQ-4122', type: '3way', angle: 90,
    description: '3-Way Corner (Right Angle)',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 4.2, size_mm: 290,
  },
  'SQ-4123': {
    id: 'SQ-4123', label: 'SQ-4123', type: '3way', angle: 90,
    description: '3-Way Corner (Left Angle)',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 4.2, size_mm: 290,
  },
  'SQ-4126': {
    id: 'SQ-4126', label: 'SQ-4126', type: '4way', angle: 90,
    description: '4-Way Cross Junction',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 5.8, size_mm: 290,
  },
  'SQ-4127': {
    id: 'SQ-4127', label: 'SQ-4127', type: '4way', angle: 90,
    description: '4-Way T-Junction Block',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 5.5, size_mm: 290,
  },
  'SQ-4129': {
    id: 'SQ-4129', label: 'SQ-4129', type: '4way', angle: 90,
    description: '4-Way Box Corner',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 6.2, size_mm: 290,
  },
  'SQ-4130': {
    id: 'SQ-4130', label: 'SQ-4130', type: '5way', angle: 90,
    description: '5-Way Corner Block',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 7.5, size_mm: 290,
  },
  'SQ-4131': {
    id: 'SQ-4131', label: 'SQ-4131', type: '6way', angle: 90,
    description: '6-Way Corner Block (Full Cube)',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 8.8, size_mm: 290,
  },
  'SQ-4136': {
    id: 'SQ-4136', label: 'SQ-4136', type: '2way', angle: 60,
    description: '2-Way 60° Corner Block',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 3.2, size_mm: 290,
  },

  // --- DT44P compatible (400mm Square Heavy) ---
  'DT-4109': {
    id: 'DT-4109', label: 'DT-4109', type: '2way', angle: 90,
    description: '2-Way 90° (400mm)',
    compatibleProfiles: ['DT44P'],
    weight: 4.2, size_mm: 400,
  },
  'DT-4121': {
    id: 'DT-4121', label: 'DT-4121', type: '3way', angle: 90,
    description: '3-Way T-Junction (400mm)',
    compatibleProfiles: ['DT44P'],
    weight: 6.5, size_mm: 400,
  },
  'DT-4126': {
    id: 'DT-4126', label: 'DT-4126', type: '4way', angle: 90,
    description: '4-Way Cross (400mm)',
    compatibleProfiles: ['DT44P'],
    weight: 8.2, size_mm: 400,
  },
  'DT-4129': {
    id: 'DT-4129', label: 'DT-4129', type: '4way', angle: 90,
    description: '4-Way Box Corner (400mm)',
    compatibleProfiles: ['DT44P'],
    weight: 9.0, size_mm: 400,
  },

  // --- F24 compatible (220mm Square) ---
  'SQ-F24-2W': {
    id: 'SQ-F24-2W', label: 'F24-2Way', type: '2way', angle: 90,
    description: '2-Way 90° (220mm)',
    compatibleProfiles: ['F24'],
    weight: 1.8, size_mm: 220,
  },
  'SQ-F24-3W': {
    id: 'SQ-F24-3W', label: 'F24-3Way', type: '3way', angle: 90,
    description: '3-Way Corner (220mm)',
    compatibleProfiles: ['F24'],
    weight: 2.8, size_mm: 220,
  },
  'SQ-F24-4W': {
    id: 'SQ-F24-4W', label: 'F24-4Way', type: '4way', angle: 90,
    description: '4-Way Box Corner (220mm)',
    compatibleProfiles: ['F24'],
    weight: 3.8, size_mm: 220,
  },

  // --- F14 compatible (100mm Square) ---
  'SQ-F14-2W': {
    id: 'SQ-F14-2W', label: 'F14-2Way', type: '2way', angle: 90,
    description: '2-Way 90° (100mm)',
    compatibleProfiles: ['F14'],
    weight: 0.6, size_mm: 100,
  },
  'SQ-F14-3W': {
    id: 'SQ-F14-3W', label: 'F14-3Way', type: '3way', angle: 90,
    description: '3-Way Corner (100mm)',
    compatibleProfiles: ['F14'],
    weight: 0.9, size_mm: 100,
  },
  'SQ-F14-4W': {
    id: 'SQ-F14-4W', label: 'F14-4Way', type: '4way', angle: 90,
    description: '4-Way Box Corner (100mm)',
    compatibleProfiles: ['F14'],
    weight: 1.2, size_mm: 100,
  },

  // --- Ladder/I-Beam F32 ---
  'F32-2W': {
    id: 'F32-2W', label: 'F32-2Way', type: '2way', angle: 90,
    description: '2-Way 90° Ladder (290mm)',
    compatibleProfiles: ['F32'],
    weight: 2.2, size_mm: 290,
  },
  'F32-3W': {
    id: 'F32-3W', label: 'F32-3Way', type: '3way', angle: 90,
    description: '3-Way Corner Ladder (290mm)',
    compatibleProfiles: ['F32'],
    weight: 3.4, size_mm: 290,
  },
  'F32-4W': {
    id: 'F32-4W', label: 'F32-4Way', type: '4way', angle: 90,
    description: '4-Way Cross Ladder (290mm)',
    compatibleProfiles: ['F32'],
    weight: 4.5, size_mm: 290,
  },

  // --- Motor Truss Corner Blocks (with top plate adapter for hoist/motor mounting) ---
  'SQ-4109-MT': {
    id: 'SQ-4109-MT', label: 'SQ-4109-MT', type: '2way', angle: 90,
    description: '2-Way Motor Corner with Top Plate (290mm)',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 4.5, size_mm: 290,
    isMotorCorner: true,
  },
  'SQ-4129-MT': {
    id: 'SQ-4129-MT', label: 'SQ-4129-MT', type: '4way', angle: 90,
    description: '4-Way Motor Corner with Top Plate (290mm)',
    compatibleProfiles: ['F34', 'DT34P'],
    weight: 8.8, size_mm: 290,
    isMotorCorner: true,
  },
  'DT-4109-MT': {
    id: 'DT-4109-MT', label: 'DT-4109-MT', type: '2way', angle: 90,
    description: '2-Way Motor Corner with Top Plate (400mm)',
    compatibleProfiles: ['DT44P'],
    weight: 6.5, size_mm: 400,
    isMotorCorner: true,
  },
  'DT-4129-MT': {
    id: 'DT-4129-MT', label: 'DT-4129-MT', type: '4way', angle: 90,
    description: '4-Way Motor Corner with Top Plate (400mm)',
    compatibleProfiles: ['DT44P'],
    weight: 11.5, size_mm: 400,
    isMotorCorner: true,
  },
};

// =============================================
// BASE PLATE CATALOG (Global Truss)
// =============================================
export const BASE_PLATES = {
  'SQ-4137': {
    id: 'SQ-4137', label: 'SQ-4137',
    description: 'Base Plate 600x600mm',
    size_mm: 600, thickness_mm: 8, weight: 5.5,
    maxLoad_kg: 1500,
    compatibleProfiles: ['F34', 'DT34P'],
    hasOutriggers: false,
  },
  'SQ-4137-BLK': {
    id: 'SQ-4137-BLK', label: 'SQ-4137 Black',
    description: 'Base Plate 600x600mm (Black)',
    size_mm: 600, thickness_mm: 8, weight: 5.5,
    maxLoad_kg: 1500,
    compatibleProfiles: ['F34', 'DT34P'],
    hasOutriggers: false,
  },
  'SQ-4141': {
    id: 'SQ-4141', label: 'SQ-4141',
    description: 'Base Plate with Ground Slots 600x600mm',
    size_mm: 600, thickness_mm: 10, weight: 6.2,
    maxLoad_kg: 2000,
    compatibleProfiles: ['F34', 'DT34P'],
    hasOutriggers: true,
  },
  'SQ-4145': {
    id: 'SQ-4145', label: 'SQ-4145',
    description: 'Base Plate 800x800mm Heavy Duty',
    size_mm: 800, thickness_mm: 12, weight: 12.0,
    maxLoad_kg: 3500,
    compatibleProfiles: ['F34', 'DT34P', 'DT44P'],
    hasOutriggers: true,
  },
  'DT-4164': {
    id: 'DT-4164', label: 'DT-4164',
    description: 'Base Plate 800x800mm (400mm Truss)',
    size_mm: 800, thickness_mm: 12, weight: 14.0,
    maxLoad_kg: 4000,
    compatibleProfiles: ['DT44P'],
    hasOutriggers: true,
  },
  'SQ-4187': {
    id: 'SQ-4187', label: 'SQ-4187',
    description: 'Base Plate 450x450mm Standard',
    size_mm: 450, thickness_mm: 6, weight: 3.2,
    maxLoad_kg: 800,
    compatibleProfiles: ['F24', 'F14'],
    hasOutriggers: false,
  },
  'SQ-4188': {
    id: 'SQ-4188', label: 'SQ-4188',
    description: 'Base Plate 450x450mm with Outriggers',
    size_mm: 450, thickness_mm: 8, weight: 4.8,
    maxLoad_kg: 1200,
    compatibleProfiles: ['F24', 'F14'],
    hasOutriggers: true,
  },
  'SQ-4139': {
    id: 'SQ-4139', label: 'SQ-4139',
    description: 'Truss Adapter / Top Plate 290mm',
    size_mm: 350, thickness_mm: 6, weight: 2.0,
    maxLoad_kg: 1000,
    compatibleProfiles: ['F34', 'DT34P', 'F32', 'F33'],
    hasOutriggers: false,
  },
  'F14-BP': {
    id: 'F14-BP', label: 'F14-BP',
    description: 'Base Plate 250x250mm (Mini Truss)',
    size_mm: 250, thickness_mm: 5, weight: 1.2,
    maxLoad_kg: 400,
    compatibleProfiles: ['F14'],
    hasOutriggers: false,
  },
  'F32-BP': {
    id: 'F32-BP', label: 'F32-BP',
    description: 'Base Plate 500x250mm (Ladder)',
    size_mm: 500, thickness_mm: 8, weight: 4.0,
    maxLoad_kg: 1200,
    compatibleProfiles: ['F32'],
    hasOutriggers: false,
  },
};

/**
 * Get corner blocks compatible with a given truss profile
 */
export function getCompatibleCornerBlocks(profileId) {
  return Object.values(CORNER_BLOCKS).filter(
    cb => cb.compatibleProfiles.includes(profileId)
  );
}

/**
 * Get base plates compatible with a given truss profile
 */
export function getCompatibleBasePlates(profileId) {
  return Object.values(BASE_PLATES).filter(
    bp => bp.compatibleProfiles.includes(profileId)
  );
}

// =============================================
// VALIDATION & SUGGESTION FUNCTIONS
// =============================================

/**
 * Validate a span length against standard truss sizes.
 * Returns validation result with closest alternatives and build suggestions.
 */
export function validateSpanLength(profileId, requestedLength) {
  const profile = TRUSS_LIBRARY[profileId];
  if (!profile) return { valid: false, message: 'Unknown profile' };

  const available = profile.availableLengths;
  const tolerance = 0.01; // 1cm tolerance

  // Check if exact length is a standard size
  const isExact = available.some(l => Math.abs(l - requestedLength) < tolerance);
  if (isExact) {
    return {
      valid: true,
      exact: true,
      length: requestedLength,
      message: `Standard ${requestedLength}m segment available`,
      combinations: [[requestedLength]],
    };
  }

  // Find closest smaller and larger standard sizes
  const smaller = available.filter(l => l <= requestedLength + tolerance).sort((a, b) => b - a);
  const larger = available.filter(l => l >= requestedLength - tolerance).sort((a, b) => a - b);

  const closestSmaller = smaller[0] || null;
  const closestLarger = larger[0] || null;

  // Find combination of standard sizes that equals or closely matches requested length
  const combinations = findCombinations(available, requestedLength, tolerance);

  // Max possible span from load table
  const maxSpan = profile.loadTable[profile.loadTable.length - 1].span;

  if (requestedLength > maxSpan) {
    return {
      valid: false,
      exact: false,
      length: requestedLength,
      message: `Exceeds max rated span of ${maxSpan}m for ${profile.label}`,
      closestSmaller,
      closestLarger: maxSpan,
      combinations: [],
    };
  }

  return {
    valid: combinations.length > 0,
    exact: false,
    length: requestedLength,
    message: combinations.length > 0
      ? `Can be built with ${combinations[0].join(' + ')} = ${combinations[0].reduce((a, b) => a + b, 0)}m`
      : `Exact ${requestedLength}m not achievable with standard sizes`,
    closestSmaller,
    closestLarger,
    combinations,
  };
}

/**
 * Find combinations of standard lengths that sum to the target (within tolerance).
 * Returns up to 3 best combinations.
 */
function findCombinations(available, target, tolerance) {
  const results = [];
  const maxPieces = 8;
  const sorted = [...available].sort((a, b) => b - a);

  // Try to find exact match combos using dynamic programming approach
  function search(remaining, combo, startIdx) {
    if (Math.abs(remaining) < tolerance) {
      results.push([...combo]);
      return;
    }
    if (remaining < -tolerance || combo.length >= maxPieces || results.length >= 5) return;

    for (let i = startIdx; i < sorted.length; i++) {
      if (sorted[i] <= remaining + tolerance) {
        combo.push(sorted[i]);
        search(remaining - sorted[i], combo, i);
        combo.pop();
        if (results.length >= 5) return;
      }
    }
  }

  search(target, [], 0);

  // Sort by fewest pieces
  results.sort((a, b) => a.length - b.length);
  return results.slice(0, 3);
}

/**
 * Interpolate load data for an arbitrary span length.
 * Uses linear interpolation between known data points.
 */
export function interpolateLoad(profileId, spanLength) {
  const profile = TRUSS_LIBRARY[profileId];
  if (!profile) return null;

  const table = profile.loadTable;
  if (spanLength <= 0) return null;

  // Below minimum span
  if (spanLength <= table[0].span) {
    return { ...table[0], interpolated: false };
  }

  // Above maximum span
  if (spanLength >= table[table.length - 1].span) {
    return { ...table[table.length - 1], interpolated: false, exceedsMax: true };
  }

  // Find bracketing entries
  for (let i = 0; i < table.length - 1; i++) {
    if (spanLength >= table[i].span && spanLength <= table[i + 1].span) {
      const t = (spanLength - table[i].span) / (table[i + 1].span - table[i].span);
      return {
        span: spanLength,
        udl: Math.round(table[i].udl + t * (table[i + 1].udl - table[i].udl)),
        pointLoad: Math.round(table[i].pointLoad + t * (table[i + 1].pointLoad - table[i].pointLoad)),
        deflection: Math.round((table[i].deflection + t * (table[i + 1].deflection - table[i].deflection)) * 10) / 10,
        interpolated: true,
        exceedsMax: false,
      };
    }
  }

  return null;
}

/**
 * Calculate load ratio and status color
 * @param {number} appliedLoad - Applied load in kg
 * @param {number} maxSafeLoad - Maximum safe load in kg
 * @returns {{ ratio, status, color }}
 */
export function getLoadStatus(appliedLoad, maxSafeLoad) {
  if (maxSafeLoad <= 0) return { ratio: 1, status: 'OVERLOAD', color: 'red' };
  const ratio = appliedLoad / maxSafeLoad;
  if (ratio < 0.7) return { ratio, status: 'SAFE', color: 'green' };
  if (ratio <= 1.0) return { ratio, status: 'WARNING', color: 'yellow' };
  return { ratio, status: 'OVERLOAD', color: 'red' };
}

/**
 * Get all profiles as an array for dropdown display
 */
export function getProfileList() {
  return Object.values(TRUSS_LIBRARY).map(p => ({
    id: p.id,
    label: p.label,
    geometry: p.geometry,
    size_mm: p.size_mm,
    weightPerMeter: p.weightPerMeter,
    allowedStructures: p.allowedStructures,
  }));
}

/**
 * Get profiles compatible with a given structure type.
 * Box/Goalpost require 'box'/'goalpost' in allowedStructures.
 * Straight/Circular/Angle accept any profile with 'single'.
 */
export function getProfilesForStructure(structureType) {
  return Object.values(TRUSS_LIBRARY).filter(p => {
    switch (structureType) {
      case 'box_system':
        return p.allowedStructures.includes('box');
      case 'goalpost':
        return p.allowedStructures.includes('goalpost');
      default:
        return true; // straight, circular, angle accept all profiles
    }
  }).map(p => ({
    id: p.id,
    label: p.label,
    geometry: p.geometry,
    size_mm: p.size_mm,
    weightPerMeter: p.weightPerMeter,
  }));
}

/**
 * Auto-determine the correct junction type based on structure.
 * Goalpost = 2way, Box = 4way, Angle = 2way, others = straight
 */
export function getAutoJunctionType(structureType) {
  switch (structureType) {
    case 'goalpost': return '2way';
    case 'box_system': return '4way';
    case 'angle': return '2way';
    case 'circular': return 'straight';
    case 'straight':
    default: return 'straight';
  }
}

/**
 * Get corner blocks filtered by profile AND junction type.
 */
export function getFilteredCornerBlocks(profileId, junctionType) {
  return Object.values(CORNER_BLOCKS).filter(cb => {
    if (!cb.compatibleProfiles.includes(profileId)) return false;
    if (junctionType && cb.type !== junctionType) return false;
    return true;
  });
}

/**
 * Calculate optimal section breakdown for a span length.
 * Returns sections array plus metadata.
 * If userSections is provided, validates it instead of auto-calculating.
 */
export function calculateSections(profileId, spanLength, userSections) {
  const profile = TRUSS_LIBRARY[profileId];
  if (!profile) return { sections: [spanLength], totalLength: spanLength, valid: false, jointCount: 0 };

  // If user provided manual sections, validate them
  if (userSections && userSections.length > 0) {
    const total = Math.round(userSections.reduce((a, b) => a + b, 0) * 100) / 100;
    const allValid = userSections.every(s => profile.availableLengths.includes(s));
    return {
      sections: userSections,
      totalLength: total,
      valid: Math.abs(total - spanLength) < 0.02 && allValid,
      jointCount: Math.max(0, userSections.length - 1),
      isOverride: true,
    };
  }

  // Check if exact standard size
  if (profile.availableLengths.includes(spanLength)) {
    return { sections: [spanLength], totalLength: spanLength, valid: true, jointCount: 0 };
  }

  // Use existing combination finder
  const validation = validateSpanLength(profileId, spanLength);
  if (validation.combinations && validation.combinations.length > 0) {
    const best = validation.combinations[0];
    return {
      sections: best,
      totalLength: Math.round(best.reduce((a, b) => a + b, 0) * 100) / 100,
      valid: true,
      jointCount: Math.max(0, best.length - 1),
    };
  }

  // Fallback: treat as single section
  return { sections: [spanLength], totalLength: spanLength, valid: false, jointCount: 0 };
}

/**
 * Auto-calculate recommended leg height based on load and structure.
 * Returns suggested minimum leg/pillar height for stability.
 */
export function calculateAutoLegSize(profileId, appliedLoad, structureType, dimensions) {
  const profile = TRUSS_LIBRARY[profileId];
  if (!profile) return { legHeight: 5, legProfile: profileId };

  // Base height depends on structure
  let baseHeight = 5;
  if (structureType === 'goalpost') {
    baseHeight = Math.max(3, (dimensions?.topWidth || 6) * 0.8);
  } else if (structureType === 'box_system') {
    const maxSpan = Math.max(dimensions?.width || 8, dimensions?.depth || 6);
    baseHeight = Math.max(4, maxSpan * 0.75);
  }

  // Adjust based on load — heavier loads need taller pillars for stability (engineering heuristic)
  const loadData = interpolateLoad(profileId, Math.max(dimensions?.width || 6, dimensions?.depth || 6));
  if (loadData && appliedLoad > 0) {
    const ratio = appliedLoad / (loadData.pointLoad || 1);
    if (ratio > 0.8) baseHeight = Math.max(baseHeight, baseHeight * 1.1);
  }

  return {
    legHeight: Math.round(baseHeight * 10) / 10,
    legProfile: profileId,
  };
}

